import { useState, useEffect } from 'react';

import { useCreateRole } from '@/api/mutations/useCreateRole';
import { useDeleteRole } from '@/api/mutations/useDeleteRole';
import { useUpdateRole } from '@/api/mutations/useUpdateRole';
import { useGetRoles, Role } from '@/api/queries/useGetRoles';
import { PERMISSION_GROUPS } from '@/constants/permissionGroups';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Input } from '@/new/components/shadcn/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';

export const Roles = () => {
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editedPermissions, setEditedPermissions] = useState<string[]>([]);

  const { data: roles = [], isLoading } = useGetRoles();
  const { mutate: createRole, isLoading: isCreating } = useCreateRole();

  const selectRole = (role: Role | null) => {
    setSelectedRole(role);
    if (role) {
      setEditedName(role.name);
      setEditedPermissions(role.permissions);
    } else {
      setEditedName('');
      setEditedPermissions([]);
    }
  };

  // Auto-select the admin role when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && !selectedRole) {
      const adminRole = roles.find((r) => r.name === 'Administrator');
      selectRole(adminRole ?? roles[0]);
    }
  }, [roles, selectedRole]);

  // Update selectedRole when roles are updated
  useEffect(() => {
    if (selectedRole && roles.length > 0) {
      const updatedRole = roles.find((r) => r.id === selectedRole.id);
      if (updatedRole && updatedRole !== selectedRole) {
        selectRole(updatedRole);
      }
    }
  }, [roles, selectedRole]);

  const handleRoleDeleted = () => {
    if (roles.length > 1) {
      const remainingRoles = roles.filter((r) => r.id !== selectedRole?.id);
      selectRole(remainingRoles[0] || null);
    } else {
      selectRole(null);
    }
  };

  const handleCreateRole = () => {
    createRole(
      {
        name: 'New Role',
        permissions: [],
      },
      {
        onSuccess: (newRole) => {
          selectRole(newRole);
        },
      },
    );
  };

  return (
    <div className="w-full py-6">
      <div className="flex min-h-[600px]">
        <RolesSidebar
          roles={roles}
          selectedRole={selectedRole}
          onSelectRole={selectRole}
          onCreateRole={handleCreateRole}
          isLoading={isLoading}
          isCreating={isCreating}
        />

        <RolePermissionsPanel
          role={selectedRole}
          editedName={editedName}
          editedPermissions={editedPermissions}
          onEditedNameChange={setEditedName}
          onEditedPermissionsChange={setEditedPermissions}
          onRoleDeleted={handleRoleDeleted}
        />
      </div>
    </div>
  );
};

interface RolesSidebarProps {
  roles: Role[];
  selectedRole: Role | null;
  onSelectRole: (role: Role) => void;
  onCreateRole: () => void;
  isLoading: boolean;
  isCreating?: boolean;
}

export const RolesSidebar = ({
  roles,
  selectedRole,
  onSelectRole,
  onCreateRole,
  isLoading,
  isCreating = false,
}: RolesSidebarProps) => {
  const sortedRoles = [...roles].sort((a, b) => {
    // Administrator first, then alphabetical
    if (a.name === 'Administrator' && b.name !== 'Administrator') {
      return -1;
    }

    if (b.name === 'Administrator' && a.name !== 'Administrator') {
      return 1;
    }

    return a.name.localeCompare(b.name);
  });

  if (isLoading) {
    return (
      <div className="w-1/4 border-r border-gray-200 bg-white p-4">
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-gray-200" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-1/4 border-r border-gray-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-serif text-base font-semibold text-gray-900">Roles</h3>
        <Button onClick={onCreateRole} className="text-xs" disabled={isLoading || isCreating}>
          <Icon className="lucide--plus" />
          Add
        </Button>
      </div>

      <div className="space-y-1">
        {sortedRoles.map((role) => (
          <button
            key={role.id}
            onClick={() => onSelectRole(role)}
            className={`w-full rounded-md px-3 py-2 text-left transition-colors ${
              selectedRole?.id === role.id
                ? 'border border-blue-200 bg-blue-100 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon className="lucide--shield h-4 w-4" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">{role.name}</span>
                  {role.isDefault && (
                    <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      Default
                    </span>
                  )}
                </div>

                <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                  <Icon className="lucide--users h-3 w-3" />
                  <span>
                    {role.userCount} {role.userCount === 1 ? 'user' : 'users'}
                  </span>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

interface RolePermissionsPanelProps {
  role: Role | null;
  editedName: string;
  editedPermissions: string[];
  onEditedNameChange: (name: string) => void;
  onEditedPermissionsChange: (permissions: string[]) => void;
  onRoleDeleted: () => void;
}

export const RolePermissionsPanel = ({
  role,
  editedName,
  editedPermissions,
  onEditedNameChange,
  onEditedPermissionsChange,
  onRoleDeleted,
}: RolePermissionsPanelProps) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { mutate: updateRole, isLoading: isUpdating } = useUpdateRole();
  const { mutate: deleteRole, isLoading: isDeleting } = useDeleteRole();

  const hasUnsavedChanges =
    role &&
    (editedName !== role.name || JSON.stringify(editedPermissions.sort()) !== JSON.stringify(role.permissions.sort()));

  const handlePermissionChange = (permission: string, checked: boolean) => {
    if (checked) {
      onEditedPermissionsChange([...editedPermissions, permission]);
    } else {
      onEditedPermissionsChange(editedPermissions.filter((p) => p !== permission));
    }
  };

  const handleGroupPermissionChange = (groupPermissions: string[], checked: boolean) => {
    if (checked) {
      const newPermissions = [...editedPermissions];
      groupPermissions.forEach((permission) => {
        if (!newPermissions.includes(permission)) {
          newPermissions.push(permission);
        }
      });

      onEditedPermissionsChange(newPermissions);
    } else {
      onEditedPermissionsChange(editedPermissions.filter((p) => !groupPermissions.includes(p)));
    }
  };

  const handleSave = () => {
    if (!role || !editedName.trim() || editedName.length > 50) {
      return;
    }

    updateRole({
      roleId: role.id,
      name: editedName.trim(),
      permissions: editedPermissions,
    });
  };

  const handleDiscard = () => {
    if (role) {
      onEditedNameChange(role.name);
      onEditedPermissionsChange(role.permissions);
    }
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (role) {
      deleteRole(role.id, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          onRoleDeleted();
        },
      });
    }
  };

  if (!role) {
    return null;
  }

  const isAdministrator = role.name === 'Administrator';
  const canEdit = !isAdministrator;
  const canDelete = !isAdministrator && !role.isDefault && role.userCount === 0;

  const getDeleteTooltip = () => {
    if (role.isDefault) {
      return 'The default role cannot be deleted';
    }

    if (role.userCount > 0) {
      return `Roles can't be deleted while assigned to users`;
    }

    return '';
  };

  return (
    <div className="flex-1 px-6 py-4">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {isEditingName ? (
                    <div className="max-w-sm">
                      <Input
                        value={editedName}
                        onChange={(e) => {
                          onEditedNameChange(e.target.value);
                        }}
                        placeholder="Enter role name"
                        maxLength={50}
                        disabled={isUpdating}
                        className="text-2xl font-bold"
                        onBlur={() => setIsEditingName(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setIsEditingName(false);
                            handleSave();
                          }

                          if (e.key === 'Escape') {
                            onEditedNameChange(role.name);
                            setIsEditingName(false);
                          }
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h1 className="font-serif text-lg font-semibold text-gray-900">{role.name}</h1>

                      {canEdit && (
                        <button
                          onClick={() => setIsEditingName(true)}
                          className="text-gray-400 transition-colors hover:text-gray-600"
                          disabled={isUpdating}
                        >
                          <Icon className="lucide--pencil-line h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <p className="text-sm text-gray-600">
                    {role.userCount} {role.userCount === 1 ? 'user' : 'users'} • {editedPermissions.length} permissions
                  </p>
                  {hasUnsavedChanges && <span className="text-sm font-medium text-orange-600">• Unsaved changes</span>}
                </div>
              </div>

              <div className="flex gap-2">
                {canEdit && (
                  <>
                    <Button variant="primary" onClick={handleSave} disabled={isUpdating || !hasUnsavedChanges}>
                      <Icon className="lucide--save" />
                      Save
                    </Button>
                    {hasUnsavedChanges && (
                      <Button onClick={handleDiscard} disabled={isUpdating}>
                        Discard
                      </Button>
                    )}
                  </>
                )}

                {!isAdministrator && (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <span className={!canDelete ? 'cursor-not-allowed' : ''}>
                          <Button onClick={handleDeleteClick} disabled={!canDelete}>
                            <Icon className="lucide--trash-2" />
                            Delete
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!canDelete && (
                        <TooltipContent>
                          <p>{getDeleteTooltip()}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-start justify-between border-b border-gray-200 p-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Select permissions</h3>
                <p className="mt-1 text-xs text-gray-500">
                  Permissions define what actions users with this role can perform.
                </p>
              </div>

              {isAdministrator && (
                <div className="text-right">
                  <p className="rounded bg-blue-50 px-2 py-1 text-xs text-blue-700">
                    Administrator role cannot be modified
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-1 p-4">
              {PERMISSION_GROUPS.map((group) => {
                const groupPermissions = group.permissions.map((p) => p.permission);
                const selectedGroupPermissions = groupPermissions.filter((p) => editedPermissions.includes(p));
                const isGroupFullySelected = selectedGroupPermissions.length === groupPermissions.length;

                return (
                  <div key={group.name} className="space-y-1">
                    {/* Group header */}
                    <div className="flex items-center rounded px-2 py-2 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isGroupFullySelected}
                          onCheckedChange={(checked) => handleGroupPermissionChange(groupPermissions, Boolean(checked))}
                          disabled={isAdministrator || isUpdating || !canEdit}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-900">{group.name}</span>
                      </div>
                    </div>

                    {/* Individual permissions */}
                    {group.permissions.map((permissionInfo) => (
                      <div
                        key={permissionInfo.permission}
                        className="ml-6 flex items-center justify-between rounded px-2 py-2 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={editedPermissions.includes(permissionInfo.permission)}
                            onCheckedChange={(checked) =>
                              handlePermissionChange(permissionInfo.permission, Boolean(checked))
                            }
                            disabled={isAdministrator || isUpdating || !canEdit}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <span className="font-mono text-sm text-gray-600">{permissionInfo.permission}</span>
                        </div>
                        <div className="max-w-md text-right text-sm text-gray-500">{permissionInfo.description}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        title="Delete Role"
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      >
        <p>Are you sure you want to delete the role "{role.name}"? This action cannot be undone.</p>
      </ConfirmationModal>
    </div>
  );
};
